import type { PushData, PushPermission, PushPlatform, PushToken } from 'src/entities/push'
import type { IPushBridge } from 'src/use-cases/push/ports/IPushBridge'
import { isPushPermission } from 'src/entities/push'
import { isAndroid, isIOS } from 'utils/platform'

// Ответ на запрос токена ждём долго: пользователь может не сразу ответить на диалог разрешения.
const TOKEN_TIMEOUT = 120_000
// Разрешение обёртка отдаёт сразу, без диалога.
const PERMISSION_TIMEOUT = 5_000
const TOPIC_TIMEOUT = 30_000

type Method
  = | 'getPushToken'
    | 'deletePushToken'
    | 'getInitialPush'
    | 'getPushPermission'
    | 'openNotificationSettings'

// «Вилка»: пуши через мост нативной обёртки (seller-android README, «Push-уведомления»; MechtaSeller-ios PUSH.md).
// Обёртка отвечает вызовами window.onPushToken / onPushTokenError / onPushOpened / onPushTokenChanged /
// onPushPermission.
export class NativePushBridge implements IPushBridge {
  #token: Promise<PushToken> | null = null
  #topics = new Map<string, { state: string, finish: (error?: Error) => void }>()
  #permissionWaiters: Array<(permission: PushPermission) => void> = []
  #permissionListeners = new Set<(permission: PushPermission) => void>()

  constructor() {
    if (typeof window === 'undefined') return
    window.onPushTopic = (topic, state) => {
      const pending = this.#topics.get(topic)
      if (!pending) return
      if (state === 'error') pending.finish(new Error('PUSH_TOPIC_FAILED'))
      else if (state === pending.state) pending.finish()
    }
    // один обработчик на ответы getPushPermission и на события возврата в приложение
    window.onPushPermission = (value) => {
      if (!isPushPermission(value)) return
      for (const resolve of this.#permissionWaiters.splice(0)) resolve(value)
      for (const listener of this.#permissionListeners) listener(value)
    }
  }

  isAvailable() {
    return this.#platform() !== null
  }

  getToken(refresh = false) {
    if (refresh && this.#token) {
      // спросить заново после того, как текущий запрос закончится: обработчики ответа у обёртки одни
      this.#token = this.#token.catch(() => null).then(() => this.#requestToken())
      this.#forgetOnError()
    } else if (!this.#token) {
      this.#token = this.#requestToken()
      this.#forgetOnError()
    }
    return this.#token
  }

  currentToken() {
    return this.#token
  }

  deleteToken() {
    for (const pending of this.#topics.values()) pending.finish(new Error('PUSH_SESSION_ENDED'))
    this.#token = null
    this.#call('deletePushToken')
  }

  onOpened(handler: (data: PushData) => void) {
    window.onPushOpened = handler
  }

  onTokenChanged(handler: (token: string) => void) {
    window.onPushTokenChanged = (token) => {
      const platform = this.#platform()
      if (platform) this.#token = Promise.resolve({ token, platform, granted: true })
      handler(token)
    }
  }

  requestInitialPush() {
    this.#call('getInitialPush')
  }

  canManagePermission() {
    if (isAndroid() && window.AndroidJSInterface?.getPushPermission) return true
    return isIOS() && !!window.webkit?.messageHandlers?.getPushPermission?.postMessage
  }

  getPermission(): Promise<PushPermission> {
    if (!this.canManagePermission()) return Promise.reject(new Error('PUSH_PERMISSION_UNAVAILABLE'))

    return new Promise((resolve, reject) => {
      let timeout: ReturnType<typeof setTimeout> | undefined
      const waiter = (permission: PushPermission) => {
        clearTimeout(timeout)
        resolve(permission)
      }
      timeout = setTimeout(() => {
        this.#permissionWaiters = this.#permissionWaiters.filter((w) => w !== waiter)
        reject(new Error('TIMEOUT'))
      }, PERMISSION_TIMEOUT)

      this.#permissionWaiters.push(waiter)
      this.#call('getPushPermission')
    })
  }

  onPermissionChanged(handler: (permission: PushPermission) => void) {
    this.#permissionListeners.add(handler)
  }

  openSettings() {
    this.#call('openNotificationSettings')
  }

  canManageTopics() {
    if (isAndroid()) return !!window.AndroidJSInterface?.subscribePushTopic && !!window.AndroidJSInterface?.unsubscribePushTopic
    return isIOS() && !!window.webkit?.messageHandlers?.subscribePushTopic?.postMessage
      && !!window.webkit?.messageHandlers?.unsubscribePushTopic?.postMessage
  }

  subscribeTopic(topic: string) {
    return this.#changeTopic(topic, true)
  }

  unsubscribeTopic(topic: string) {
    return this.#changeTopic(topic, false)
  }

  #changeTopic(topic: string, subscribe: boolean): Promise<void> {
    if (!this.canManageTopics()) return Promise.reject(new Error('PUSH_TOPICS_UNAVAILABLE'))
    if (this.#topics.has(topic)) return Promise.reject(new Error('PUSH_TOPIC_BUSY'))
    return new Promise((resolve, reject) => {
      let timeout: ReturnType<typeof setTimeout> | undefined
      const finish = (error?: Error) => {
        clearTimeout(timeout)
        this.#topics.delete(topic)
        if (error) reject(error)
        else resolve()
      }
      timeout = setTimeout(() => finish(new Error('PUSH_TOPIC_TIMEOUT')), TOPIC_TIMEOUT)
      this.#topics.set(topic, { state: subscribe ? 'subscribed' : 'unsubscribed', finish })
      const method = subscribe ? 'subscribePushTopic' : 'unsubscribePushTopic'
      try {
        if (isAndroid()) window.AndroidJSInterface![method]!(topic)
        else window.webkit!.messageHandlers![method]!.postMessage!(topic)
      } catch {
        finish(new Error('PUSH_TOPIC_FAILED'))
      }
    })
  }

  #forgetOnError() {
    const current = this.#token
    // ошибку не кэшируем — следующий вызов спросит снова
    current?.catch(() => {
      if (this.#token === current) this.#token = null
    })
  }

  #requestToken(): Promise<PushToken> {
    const platform = this.#platform()
    if (!platform) return Promise.reject(new Error('PUSH_UNAVAILABLE'))

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        cleanup()
        reject(new Error('TIMEOUT'))
      }, TOKEN_TIMEOUT)

      function cleanup() {
        clearTimeout(timeout)
        delete window.onPushToken
        delete window.onPushTokenError
      }

      window.onPushToken = (token, granted) => {
        cleanup()
        resolve({ token, platform, granted })
      }
      window.onPushTokenError = (code) => {
        cleanup()
        reject(new Error(code))
      }

      this.#call('getPushToken')
    })
  }

  #platform(): PushPlatform | null {
    if (isAndroid() && window.AndroidJSInterface?.getPushToken) return 'android'
    if (isIOS() && window.webkit?.messageHandlers?.getPushToken?.postMessage) return 'ios'
    return null
  }

  #call(method: Method) {
    if (isAndroid() && window.AndroidJSInterface?.[method]) {
      window.AndroidJSInterface[method]!()
      return
    }
    if (isIOS()) window.webkit?.messageHandlers?.[method]?.postMessage?.('')
  }
}
